import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { getGuestId, SESSION_KEY } from "@/lib/api";
import { deviceToken, ensureCreationSession, clearCreationSession } from "@/lib/provenanceSession";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  const claimGuest = useCallback(async () => {
    try {
      await api.post("/projects/claim", { guest_id: getGuestId() });
    } catch (e) {
      /* ignore */
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      deviceToken();
      ensureCreationSession();
    } catch (e) {
      if (e?.response?.status === 401) localStorage.removeItem(SESSION_KEY);
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const storeSession = (data) => {
    if (data?.session_token) localStorage.setItem(SESSION_KEY, data.session_token);
    const { session_token, ...publicUser } = data || {};
    return publicUser;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    const u = storeSession(data);
    await claimGuest();
    setUser(u);
    return u;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name, lang: localStorage.getItem("cpenz_lang") || "EN" });
    const u = storeSession(data);
    await claimGuest();
    setUser(u);
    return u;
  };

  const finishGoogle = async (userData) => {
    const u = storeSession(userData);
    await claimGuest();
    setUser(u);
    await checkAuth();
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    localStorage.removeItem(SESSION_KEY);
    clearCreationSession();
    setUser(false);
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, finishGoogle, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
